import React, { useEffect, useState } from 'react';
import { Receipt, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { getCompanySettings, updateCompanySettings } from '../../services/api';

const EMPTY = { tps_number: '', tvq_number: '', estimate_valid_days: '30', estimate_notes: '' };

// Paramètres de l'entreprise : réservés à l'administrateur.
export const SettingsPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setForm({
      tps_number: data.tps_number || '',
      tvq_number: data.tvq_number || '',
      estimate_valid_days: String(data.estimate_valid_days || 30),
      estimate_notes: data.estimate_notes || '',
    });
    setMeta(data.updated_at ? { at: data.updated_at, by: data.updated_by_name } : null);
  };

  useEffect(() => {
    getCompanySettings()
      .then(({ data }) => apply(data))
      .catch(() => toast.error('Erreur lors du chargement des paramètres'))
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const days = parseInt(form.estimate_valid_days, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error('La validité doit être entre 1 et 365 jours');
      return;
    }
    setSaving(true);
    try {
      const { data } = await updateCompanySettings({
        tps_number: form.tps_number,
        tvq_number: form.tvq_number,
        estimate_valid_days: days,
        estimate_notes: form.estimate_notes,
      });
      apply(data);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1a3a5c]">Paramètres</h1>
        <p className="text-slate-500">Informations de l'entreprise utilisées sur les soumissions</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#00b4d8]" />
              Taxes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tps">Numéro de TPS</Label>
                <Input id="tps" value={form.tps_number} onChange={set('tps_number')} maxLength={40}
                  placeholder="123456789 RT0001" data-testid="settings-tps" />
              </div>
              <div>
                <Label htmlFor="tvq">Numéro de TVQ</Label>
                <Input id="tvq" value={form.tvq_number} onChange={set('tvq_number')} maxLength={40}
                  placeholder="1234567890 TQ0001" data-testid="settings-tvq" />
              </div>
            </div>
            <p className="text-sm text-slate-500">Affichés dans l'en-tête de chaque soumission PDF. Laissez vide pour ne rien afficher.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Soumissions : valeurs par défaut</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-w-[12rem]">
              <Label htmlFor="valid-days">Validité (jours)</Label>
              <Input id="valid-days" value={form.estimate_valid_days} inputMode="numeric"
                onChange={(e) => setForm({ ...form, estimate_valid_days: e.target.value.replace(/\D/g, '') })} />
            </div>
            <div>
              <Label htmlFor="notes">Notes et conditions</Label>
              <Textarea id="notes" value={form.estimate_notes} onChange={set('estimate_notes')} maxLength={4000}
                className="min-h-[120px]" placeholder="Ex. : Acompte de 30 % à la signature. Garantie 1 an sur la main-d'œuvre."
                data-testid="settings-notes" />
              <p className="text-sm text-slate-500 mt-1">Préremplies dans chaque nouveau devis; modifiables devis par devis.</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-400">
            {meta ? `Dernière modification : ${new Date(meta.at).toLocaleString('fr-CA')}${meta.by ? ` par ${meta.by}` : ''}` : ''}
          </p>
          <Button type="submit" disabled={saving} className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="settings-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsPage;
